import { motion } from "framer-motion";
import { Crosshair, Video, Zap, Bot } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: Crosshair,
    title: "Strategy First",
    description:
      "We map your goals, analyze your competition, and reverse-engineer a growth plan tailored to your brand.",
  },
  {
    number: "02",
    icon: Video,
    title: "Content That Performs",
    description:
      "Professional visuals and storytelling that stops the scroll. Reels, carousels, and stories designed to convert.",
  },
  {
    number: "03",
    icon: Zap,
    title: "Smart Distribution",
    description:
      "Optimized posting schedules, hashtag strategies, and platform-specific content to maximize your reach.",
  },
  {
    number: "04",
    icon: Bot,
    title: "AI Automations",
    description:
      "Tools and workflows that save you hours every week and bring qualified leads straight to your inbox.",
  },
];

export default function HowItWorks() {
  return (
    <section
      data-testid="how-it-works-section"
      id="about"
      className="py-24 md:py-32 bg-dark-secondary"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            data-testid="how-it-works-title"
            className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl uppercase text-white"
          >
            Our System & <span className="text-lime">How It Works</span>
          </h2>
          <p className="font-body text-white/60 mt-4 max-w-xl mx-auto">
            A proven 4-pillar approach to scaling your brand.
          </p>
        </motion.div>

        {/* Steps with tracing beam */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-white/10 md:-translate-x-1/2" />

          <div className="space-y-12 md:space-y-16">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`step-${index}`}
                className={`relative flex items-start gap-6 md:gap-12 ${
                  index % 2 === 0
                    ? "md:flex-row"
                    : "md:flex-row-reverse md:text-right"
                }`}
              >
                {/* Number Circle */}
                <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 w-12 h-12 bg-dark border-2 border-lime flex items-center justify-center z-10">
                  <span className="font-heading font-bold text-lime text-sm">
                    {step.number}
                  </span>
                </div>

                {/* Content */}
                <div
                  className={`ml-20 md:ml-0 md:w-5/12 ${
                    index % 2 === 0 ? "md:pr-16" : "md:pl-16"
                  }`}
                >
                  <div
                    className={`flex items-center gap-3 mb-3 ${
                      index % 2 !== 0 ? "md:justify-end" : ""
                    }`}
                  >
                    <step.icon className="w-5 h-5 text-lime" />
                    <h3 className="font-heading font-bold text-xl uppercase text-white">
                      {step.title}
                    </h3>
                  </div>
                  <p className="font-body text-white/60 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
